import React, { useEffect, useMemo, useState } from "react";
import { History } from "lucide-react";
import api from "@/lib/api";
import { EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { prettyDate, to12 } from "@/components/LeadPractice";
import { AttendanceDialog, AttendanceSummary, attKey } from "@/components/LeadAttendance";

const localToday = () => new Date().toLocaleDateString("en-CA");

function DancerTotals({ rows, dancer }) {
  const totals = useMemo(() => {
    const m = {};
    rows.forEach((r) => (r.student_ids || []).forEach((d, i) => {
      if (dancer && d !== dancer) return;
      const t = (m[d] ||= { name: r.student_names[i] || "Dancer", present: 0, absent: 0, excused: 0, unmarked: 0, total: 0 });
      t[attKey(r.attendance?.[d]?.status) || "unmarked"] += 1; t.total += 1;
    }));
    return Object.entries(m).sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [rows, dancer]);
  if (!totals.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto mb-5" data-testid="lead-att-totals">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase text-muted-foreground"><th className="p-3">Dancer</th><th className="p-3">Practices</th><th className="p-3">Present</th><th className="p-3">Absent</th><th className="p-3">Excused</th><th className="p-3">Not marked</th><th className="p-3" title="Present out of present + absent (excused not counted)">Participation</th></tr></thead>
        <tbody className="divide-y">{totals.map(([id, t]) => {
          const counted = t.present + t.absent;
          return (
            <tr key={id} data-testid={`lead-att-total-${id}`}><td className="p-3 font-semibold">{t.name}</td><td className="p-3">{t.total}</td>
              <td className="p-3 text-teal-dark font-semibold">{t.present}</td><td className="p-3 text-red-600">{t.absent}</td><td className="p-3 text-blue-700">{t.excused}</td><td className="p-3 text-muted-foreground">{t.unmarked}</td>
              <td className="p-3 font-semibold" data-testid={`lead-att-rate-${id}`}>{counted ? `${Math.round((t.present / counted) * 100)}%` : "—"}</td></tr>); })}</tbody>
      </table>
    </div>);
}

export function LeadAttendanceHistory() {
  const [rows, setRows] = useState([]);
  const [all, setAll] = useState([]);
  const [dancer, setDancer] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/production/lead-attendance", { params: { dancer_id: dancer, date_from: from, date_to: to, today: localToday() } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { api.get("/production/lead-attendance", { params: { today: localToday() } }).then((r) => setAll(r.data)).catch(() => {}); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dancer, from, to]);
  const dancers = useMemo(() => {
    const m = new Map();
    all.forEach((r) => r.student_ids.forEach((d, i) => r.student_names[i] && m.set(d, r.student_names[i])));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [all]);
  return (
    <div data-testid="lead-att-history">
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="flex flex-col gap-1.5"><Label>Lead dancer</Label>
          <select data-testid="lead-att-dancer" className="border rounded-md h-10 px-3 bg-white min-w-[180px]" value={dancer} onChange={(e) => setDancer(e.target.value)}>
            <option value="">All lead dancers</option>{dancers.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select></div>
        <div className="space-y-1.5"><Label>From</Label><Input data-testid="lead-att-from" type="date" className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div className="space-y-1.5"><Label>To</Label><Input data-testid="lead-att-to" type="date" className="w-40" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        {(dancer || from || to) && <Button variant="outline" className="rounded-full" data-testid="lead-att-clear" onClick={() => { setDancer(""); setFrom(""); setTo(""); }}>Clear</Button>}
      </div>
      {rows.length === 0 ? <EmptyState icon={History} title="No past lead practices" hint="Lead practices appear here once their date arrives." testid="lead-att-empty" /> : <>
        <DancerTotals rows={rows} dancer={dancer} />
        <div className="space-y-2">{rows.map((r) => (
          <div key={r.id} data-testid={`lead-att-row-${r.id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold">{prettyDate(r.date)} · {to12(r.start_time)}–{to12(r.end_time)}</p>
              <p className="text-xs text-muted-foreground">{[r.production_name, r.character_names.join(", "), r.student_names.filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</p>
              <AttendanceSummary r={r} />
            </div>
            <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" data-testid={`lead-att-open-${r.id}`} onClick={() => setOpen(r)}>View / correct</Button>
          </div>))}</div>
      </>}
      {open && <AttendanceDialog r={open} onClose={() => { setOpen(null); load(); }} />}
    </div>);
}
